import { token } from '@atlaskit/tokens';
import {
  VERT_DRAG_HANDLER_ICON_WIDTH,
  DRAG_HANDLER_TD_ELEMENT_WIDTH,
} from '@databases/icons';
import { defaultStoreUtils } from '@databases/redux';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import React, { type Ref, useEffect, useState } from 'react';
import styled from 'styled-components';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';

import { EmptyState } from './EmptyState';
import { NoResultsState } from './NoResultsState';

// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
const Tr = styled.tr<{ isInitialized: boolean }>`
  margin-right: 1px;
  transition: opacity 1s;
  opacity: ${(props) => (props.isInitialized ? '1' : '0')};
  border-right: ${(props) =>
    props.isVisualRefreshM1Enabled
      ? 'none'
      : `1px solid ${token('color.border', '#091E4224')}`};
`;

export interface TableLayoutEmptyStateRowProps {
  contentId: string;
  canEditContent: boolean;
  allFieldsHidden?: boolean;
}

const TableLayoutEmptyStateRow = React.memo(
  React.forwardRef<HTMLElement, TableLayoutEmptyStateRowProps>(
    ({ contentId, allFieldsHidden, canEditContent }, ref) => {
      const [isInitialized, setIsInitialized] = useState(false);
      useEffect(() => {
        setIsInitialized(true);
      }, []);

      const isVisualRefreshM1Enabled = isFeatureFlagEnabled(
        'databases-frontend-visual-refresh-table-m1'
      );
      const dragHandlerTdElementWidth = isVisualRefreshM1Enabled
        ? DRAG_HANDLER_TD_ELEMENT_WIDTH
        : VERT_DRAG_HANDLER_ICON_WIDTH;

      const hasEntries = defaultStoreUtils.useEqualSelector((state) => {
        const databaseDraft = selectDatabaseDraftByContentId(state, {
          contentId,
          includeDeleted: true,
        });

        return databaseDraft.entries.length > 0;
      });

      return (
        <Tr
          ref={ref as Ref<HTMLTableRowElement>}
          isInitialized={isInitialized}
          style={{
            gridTemplateColumns: 'auto',
            // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
            marginLeft: `${canEditContent ? dragHandlerTdElementWidth / 2 : 0}px`,
          }}
          isVisualRefreshM1Enabled={isVisualRefreshM1Enabled}
          data-testid="table-layout-empty-state"
        >
          <td
            style={{
              ...(isVisualRefreshM1Enabled && {
                borderLeft: 'none',
              }),
            }}
          >
            {hasEntries ? (
              allFieldsHidden ? (
                <EmptyState contentId={contentId} />
              ) : (
                <NoResultsState />
              )
            ) : (
              <EmptyState contentId={contentId} />
            )}
          </td>
        </Tr>
      );
    }
  )
);

export default TableLayoutEmptyStateRow;
